/**
 * Account overview — the portal's home.
 *
 * Answers, in order: is my license working, what plan am I on, and what do I do
 * next. It used to answer "how long do I have" as well; nothing is counting down
 * any more, because an account with nothing behind it is on Archis Free rather than
 * on a clock.
 */

import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { useSession } from '@/app/providers'
import { planLabel, roleLabels } from '@/entities/types'
import { useAccount, useDeleteAccount, useDevices, useOrganization } from '@/shared/api/hooks'
import { AppShell, PageHead } from '@/shared/ui/Chrome'
import {
  Badge,
  Button,
  ConfirmDialog,
  ErrorNotice,
  Field,
  Fig,
  Loading,
  Notice,
  Panel,
  Readout,
  StatusBadge,
} from '@/shared/ui/primitives'

export function Account() {
  const { data: account, isPending, error } = useAccount()
  const { data: devices } = useDevices()
  const navigate = useNavigate()
  const { signOut } = useSession()
  const deleteAccount = useDeleteAccount()
  const [deleting, setDeleting] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState('')
  const soleOwnerOf = account?.organizations.filter((org) => org.role === 'owner') ?? []
  const activeDevices = devices?.filter((d) => d.status === 'active') ?? []

  const primaryOrg = account?.organizations.find((org) => org.has_seat && org.status !== 'suspended')
    ?? account?.organizations.find((org) => org.status !== 'suspended')
    ?? account?.organizations[0]
  const isSuspended = primaryOrg?.status === 'suspended'
  const hasWorkspace = Boolean(primaryOrg) && !isSuspended
  const role = primaryOrg?.role ?? 'member'
  const isMember = hasWorkspace && role === 'member'
  const isOwner = hasWorkspace && role === 'owner'

  const { data: org } = useOrganization(primaryOrg?.id ?? '')
  const isFreeTeam = hasWorkspace && (org ? (org.plan === 'free_team' || !org.subscription_status) : (!account?.subscription_status || account?.plan === 'free_team'))
  const planCode = hasWorkspace
    ? isFreeTeam
      ? 'free_team'
      : (org?.plan ?? account?.plan ?? 'team')
    : 'free'

  return (
    <AppShell>
      <PageHead
        index="01"
        eyebrow="Account"
        title={account ? `Hello, ${firstName(account.display_name || account.email)}` : 'Account'}
        subtitle={
          isSuspended
            ? `Your seat in ${primaryOrg?.name ?? 'your workspace'} is suspended. Personal CLI entitlement.`
            : isMember
              ? `Active member of ${primaryOrg?.name ?? 'your workspace'}. Your seat and CLI entitlement.`
              : 'Your license, your plan and what the CLI is entitled to right now.'
        }
        actions={
          <Link to="/downloads">
            <Button size="sm">Install Archis</Button>
          </Link>
        }
      />

      {isPending && <Loading label="Loading your account" />}
      <ErrorNotice error={error} />

      {account && (
        <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
          {/* Role-tailored hero banner */}
          {isSuspended ? (
            <Panel tone="coral" title="Workspace Access Suspended" index="!">
              <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
                <p style={{ margin: 0 }}>
                  Your seat in <strong>{primaryOrg?.name}</strong> has been suspended by a workspace administrator.
                  You cannot access team repositories, shared graphs, or workspace settings.
                </p>
                <p className="dim" style={{ margin: 0 }}>
                  Contact your workspace Owner or Admin to reactivate your seat. In the meantime, your personal account remains active on the <strong>Archis Free</strong> tier for personal repositories.
                </p>
                <div className="row" style={{ gap: 'var(--s3)', flexWrap: 'wrap' }}>
                  <Link to="/devices">
                    <Button size="sm" variant="ghost">Manage personal devices</Button>
                  </Link>
                </div>
              </div>
            </Panel>
          ) : isMember ? (
            <Panel tone="signal" title="Workspace Seat Active" index="02">
              <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
                <p style={{ margin: 0 }}>
                  You hold an active seat in <strong>{primaryOrg?.name}</strong> on the{' '}
                  <strong>{planLabel(planCode)}</strong> plan. Your CLI commands automatically inherit
                  team repository access and organization security policies.
                </p>
                <div className="row" style={{ gap: 'var(--s3)', flexWrap: 'wrap' }}>
                  <Link to="/team">
                    <Button size="sm" variant="primary">View team workspace</Button>
                  </Link>
                  <Link to="/devices">
                    <Button size="sm" variant="ghost">Manage devices</Button>
                  </Link>
                </div>
              </div>
            </Panel>
          ) : isOwner && isFreeTeam ? (
            <Panel tone="signal" title="2-Seat Free Team Evaluation" index="02">
              <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
                <p style={{ margin: 0 }}>
                  Your workspace <strong>{primaryOrg?.name}</strong> is running on the <strong>Team Free</strong> evaluation.
                  You and a teammate have 2 included free seats to index company repositories with your AI coding agents.
                </p>
                <div className="row" style={{ gap: 'var(--s3)', flexWrap: 'wrap' }}>
                  <Link to="/team">
                    <Button size="sm">Manage team</Button>
                  </Link>
                  <Link to="/billing">
                    <Button size="sm" variant="primary">Upgrade to Team (3+ seats) →</Button>
                  </Link>
                </div>
              </div>
            </Panel>
          ) : !hasWorkspace && !account.subscription_status ? (
            <Panel tone="signal" title="Archis Free" index="!">
              <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
                <p className="dim" style={{ margin: 0 }}>
                  The whole local engine — every language frontend, every agent
                  integration, unlimited repositories of your own — on two devices,
                  for as long as you like. No card.
                </p>
                <p className="mono-note" style={{ margin: 0 }}>
                  A repository an organization owns needs a Team seat, and so does a
                  shared workspace, committed repo defaults or an audit log.
                </p>
                <div className="row" style={{ gap: 'var(--s3)' }}>
                  <Link to="/team">
                    <Button variant="primary">Start 2-seat free team</Button>
                  </Link>
                  <Link to="/pricing">
                    <Button variant="ghost">What each plan covers</Button>
                  </Link>
                </div>
              </div>
            </Panel>
          ) : null}

          {/* Entitlement summary. */}
          <div className="readout-grid">
            <div>
              <Readout
                value={planLabel(planCode).toUpperCase()}
                caption={hasWorkspace ? 'plan tier' : 'personal plan'}
                tone={account.has_access ? 'signal' : undefined}
              />
            </div>
            <div>
              {isSuspended ? (
                <div className="readout">
                  <div style={{ paddingBlock: '0.35rem' }}>
                    <Badge tone="coral">Suspended</Badge>
                  </div>
                  <div className="readout__caption">workspace status</div>
                </div>
              ) : isMember ? (
                <Readout
                  value={primaryOrg?.has_seat ? 'ASSIGNED' : 'UNSEATED'}
                  caption="your seat"
                  tone={primaryOrg?.has_seat ? 'signal' : 'amber'}
                />
              ) : hasWorkspace && isFreeTeam ? (
                <div className="readout">
                  <div style={{ paddingBlock: '0.35rem' }}>
                    <Badge tone="signal">2 Free Seats</Badge>
                  </div>
                  <div className="readout__caption">evaluation tier</div>
                </div>
              ) : (
                <div className="readout">
                  <div style={{ paddingBlock: '0.35rem' }}>
                    <StatusBadge status={account.subscription_status} />
                  </div>
                  <div className="readout__caption">subscription</div>
                </div>
              )}
            </div>
            <div>
              {primaryOrg ? (
                <Readout
                  value={primaryOrg.name}
                  caption={isSuspended ? 'suspended workspace' : `workspace · ${roleLabels[role]}`}
                />
              ) : (
                <Readout
                  value="—"
                  caption="no workspace"
                />
              )}
            </div>
            <div>
              <Readout
                value={`${activeDevices.length} active`}
                caption={isSuspended ? 'personal devices' : 'cli devices'}
                tone={activeDevices.length > 0 ? 'signal' : undefined}
              />
            </div>
          </div>

          {!account.accepted_terms && (
            <Notice tone="warn">
              <span>
                Please review the{' '}
                <Link to="/terms" className="signal">
                  Terms &amp; EULA
                </Link>
                . Continued use constitutes acceptance.
              </span>
            </Notice>
          )}

          {/* Workspaces. */}
          {account.organizations.length > 0 ? (
            <Panel
              title="Workspaces"
              index="03"
              actions={
                hasWorkspace ? (
                  <Link to="/team">
                    <Button size="sm">{isMember ? 'View' : 'Manage'}</Button>
                  </Link>
                ) : undefined
              }
              padded={false}
            >
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Workspace</th>
                      <th>Your role</th>
                      <th>Seat</th>
                    </tr>
                  </thead>
                  <tbody>
                    {account.organizations.map((org) => (
                      <tr key={org.id}>
                        <td>{org.name}</td>
                        <td>
                          <Badge>{org.role.replace('_', ' ')}</Badge>
                        </td>
                        <td>
                          {org.status === 'suspended' ? (
                            <Badge tone="coral">suspended</Badge>
                          ) : org.has_seat ? (
                            <Badge tone="signal" dot>
                              assigned
                            </Badge>
                          ) : (
                            <Badge tone="amber">no seat</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          ) : (
            <Panel
              title="Workspaces"
              index="03"
              actions={
                <Link to="/team">
                  <Button size="sm" variant="primary">
                    Create workspace
                  </Button>
                </Link>
              }
            >
              <div className="stack" style={{ ['--gap' as string]: 'var(--s3)' }}>
                <p className="dim" style={{ margin: 0, maxWidth: '62ch' }}>
                  Index your company or employer repositories with a teammate. Every account can create one
                  workspace with 2 free seats forever — no credit card required.
                </p>
                <div className="row">
                  <Link to="/team">
                    <Button variant="primary">Start 2-seat free team</Button>
                  </Link>
                </div>
              </div>
            </Panel>
          )}

          {/* Getting started, shown to anyone who has not activated a device yet. */}
          <Panel title="Connect the CLI" index="04">
            <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
              <p className="dim" style={{ margin: 0 }}>
                Run <code>chi auth login</code> in a terminal. Archis opens this portal with a
                verification code, you approve it, and the CLI receives a signed license it
                verifies locally from then on.
              </p>
              <div className="term">
                <div className="term__bar">terminal</div>
                <div className="term__body">
                  <div className="term__line">
                    <span className="term__prompt">$</span> chi auth login
                  </div>
                  <div className="term__line term__dim">
                    {'  '}Opening your browser to authenticate Archis…
                  </div>
                  <div className="term__line term__dim">
                    {'  '}Verification code: <span className="term__ok">ABCD-EFGH</span>
                  </div>
                  <div className="term__line term__dim">{'  '}Waiting for authentication…</div>
                </div>
              </div>
              <div className="row">
                <Link to="/cli/activate">
                  <Button size="sm">Enter a code</Button>
                </Link>
                <Link to="/devices">
                  <Button size="sm" variant="ghost">
                    See my devices
                  </Button>
                </Link>
              </div>
            </div>
          </Panel>

          {/* Danger zone. The one irreversible thing this page can do, at the
              bottom where nobody reaches it by accident, gated behind the typed
              address exactly as workspace deletion is gated behind the name. */}
          <Panel title="Danger zone" index="05" tone="coral">
            <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
              <p style={{ margin: 0, maxWidth: '62ch' }}>
                Deleting your account removes your profile and personal data, signs out and
                revokes every connected CLI device, and ends your membership in every
                workspace. It cannot be undone.
              </p>
              {(account.subscription_status || soleOwnerOf.length > 0) && (
                <p className="mono-note" style={{ margin: 0, maxWidth: '62ch' }}>
                  {account.subscription_status
                    ? 'An active subscription has to be cancelled in Billing first. '
                    : ''}
                  {soleOwnerOf.length > 0
                    ? 'A workspace you own with other members in it needs a new owner, or has to be deleted, first.'
                    : ''}
                </p>
              )}
              <div className="row">
                <Button variant="danger" onClick={() => setDeleting(true)}>
                  Delete account
                </Button>
              </div>
            </div>
          </Panel>

          <p className="mono-note">
            Account id <Fig>{account.id}</Fig>
          </p>
        </div>
      )}

      {account && deleting && (
        <ConfirmDialog
          title="Delete account"
          danger
          confirmLabel="Delete account"
          pending={deleteAccount.isPending}
          confirmDisabled={deleteConfirm.trim().toLowerCase() !== account.email.toLowerCase()}
          onCancel={() => {
            setDeleting(false)
            setDeleteConfirm('')
          }}
          onConfirm={() =>
            deleteAccount.mutate(deleteConfirm, {
              onSuccess: async () => {
                // The server has already revoked everything; this is the browser
                // catching up. Sign-out clears the query cache with it, so no page
                // can paint the deleted account from memory.
                await signOut()
                navigate('/', { replace: true, state: { notice: 'Your account has been deleted.' } })
              },
            })
          }
          body={
            <>
              <p style={{ margin: 0 }}>
                Your profile and personal data are permanently deleted. Every connected CLI
                device and every active session is revoked now, and you leave every
                workspace you are a member of.
              </p>
              <p className="mono-note" style={{ margin: 0 }}>
                Nothing on your machines is deleted — indexes and graphs are local. This
                cannot be undone.
              </p>
              <Field label={`Type "${account.email}" to confirm`}>
                <input
                  className="input"
                  value={deleteConfirm}
                  onChange={(event) => setDeleteConfirm(event.target.value)}
                  autoFocus
                />
              </Field>
              <ErrorNotice error={deleteAccount.error} />
            </>
          }
        />
      )}
    </AppShell>
  )
}

function firstName(value: string): string {
  const [first] = value.split(/[\s@]/)
  return first || value
}
